import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { withMemberGate } from "@/lib/with-member-gate";
import { DAYS, JOURNEY_SECTIONS } from "@/lib/breaking-free";
import { BreakingFreeProvider, useBreakingFree } from "@/components/breaking-free/state";
import { ProgressBar } from "@/components/breaking-free/Shared";

export const Route = createFileRoute("/breaking-free/journey")({
  head: () => ({
    meta: [
      { title: "My Breaking Free Journey | Recovery Trauma™" },
      {
        name: "description",
        content: "Your own private reflections from the Breaking Free trauma bond recovery journey.",
      },
      { property: "og:title", content: "My Breaking Free Journey" },
      {
        property: "og:description",
        content: "Your own private reflections from the Breaking Free journey.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: withMemberGate(JourneyRoute, "Breaking Free"),
});

function JourneyRoute() {
  return (
    <BreakingFreeProvider>
      <JourneyPage />
    </BreakingFreeProvider>
  );
}

function renderValue(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === "string") return value.trim() ? [value] : [];
  if (Array.isArray(value)) return value.flatMap((v) => renderValue(v));
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .filter(([key]) => key !== "id" && key !== "savedAt")
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
      .flatMap(([, v]) => renderValue(v));

  }
  return [];
}

function JourneyPage() {
  const { loading, error, refetch, responses, completedDays } = useBreakingFree();

  const sectionKeys = new Set(JOURNEY_SECTIONS.map((s) => s.key));
  const dayWriting = DAYS.map((d) => {
    const lines = Object.entries(responses)
      .filter(([key]) => !sectionKeys.has(key) && key.startsWith(`day${d.day}-`))
      .flatMap(([, value]) => renderValue(value?.data));
    return { day: d.day, title: d.title, emoji: d.emoji ?? "🕯️", lines };
  }).filter((d) => d.lines.length > 0);


  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-16 text-center">
        <p>Something didn't load correctly.</p>
        <Button className="mt-4 min-h-[48px]" onClick={refetch}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-16 pt-8">
      <Link to="/breaking-free" className="text-sm text-muted-foreground underline">
        <span className="inline-block rtl:rotate-180">←</span> Breaking Free
      </Link>
      <h1 className="mt-4 text-2xl font-semibold sm:text-3xl">My Breaking Free Journey</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Everything you've written, gathered in one place. Private to you — never read, analysed or
        shared.
      </p>

      <div className="mt-6 rounded-3xl border border-gold/30 bg-card p-5">
        <ProgressBar done={completedDays.length} total={30} />
      </div>

      <div className="mt-8 space-y-4">
        {JOURNEY_SECTIONS.map((section) => {
          const lines = renderValue(responses[section.key]?.data);
          return (
            <section
              key={section.key}
              className="rounded-3xl border border-border bg-background p-5"
            >
              <h2 className="text-base font-semibold">
                {section.emoji} {section.title}
              </h2>
              {lines.length ? (
                <ul className="mt-3 space-y-2">
                  {lines.map((line, i) => (
                    <li
                      key={i}
                      className="rounded-2xl bg-muted/40 p-3 text-sm leading-relaxed text-foreground/85"
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  Nothing here yet — it fills as you go.
                </p>
              )}
            </section>
          );
        })}
      </div>

      <div className="mt-10">
        <h2 className="text-lg font-semibold">Day by day</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Everything else you've written, kept with the day it belongs to.
        </p>
        <div className="mt-4 space-y-4">
          {dayWriting.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing here yet — it fills as you write.
            </p>
          ) : (
            dayWriting.map(({ day, title, emoji, lines }) => (
              <section key={day} className="rounded-3xl border border-border bg-background p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-semibold">
                    {emoji} Day {day} — {title}
                  </h3>
                  <Link
                    to="/breaking-free/day/$day"
                    params={{ day: String(day) }}
                    className="shrink-0 text-sm underline"
                  >
                    Open
                  </Link>
                </div>
                <ul className="mt-3 space-y-2">
                  {lines.map((line, i) => (
                    <li
                      key={i}
                      className="rounded-2xl bg-muted/40 p-3 text-sm leading-relaxed text-foreground/85"
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

